import React, { FC, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	makeStyles,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { FormikProvider, useFormik } from 'formik';

import { AppRoutePath } from 'router/Routes';
import { useViewer, ViewerContext } from 'components/viewer/Viewer.hook';
import Viewer from 'components/viewer/Viewer';
import FormikAutocomplete from 'components/form/Autocomplete';

export type FormKeysUnion =
	| FormKeys.YEAR_OF_CENSUS
	| FormKeys.STREET_NAME
	| FormKeys.HOUSE_NUMBER
	| FormKeys.FIRST_NAME
	| FormKeys.SURNAME
	| FormKeys.INVENTORY_NUMBER
	| FormKeys.VOLUME
	| FormKeys.PAGE;

enum FormKeys {
	YEAR_OF_CENSUS = 'yearOfCensus',
	STREET_NAME = 'streetName',
	HOUSE_NUMBER = 'houseNumber',
	FIRST_NAME = 'firstName',
	SURNAME = 'surname',
	INVENTORY_NUMBER = 'inventoryNumber',
	VOLUME = 'volume',
	PAGE = 'page',
}

export type Form = { [K in FormKeysUnion as string]?: string } & {
	yearOfCensus?: string;
	streetName?: string;
	houseNumber?: string;
	firstName?: string;
	surname?: string;
	inventoryNumber?: string;
	volume?: string;
	page?: string;
};

const useStyles = makeStyles(() => ({
	pageTitle: {
		marginTop: '25px',
	},
	field: {
		margin: '5px',
	},
	fieldsContainer: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
}));

export type Order = 'ASC' | 'DESC';

type Result = {
	existOperatImage?: boolean;
	existRejstrikImage?: boolean;
	firstName?: string;
	houseNumber?: string;
	idAipv?: string;
	inventoryNumber?: string;
	numberOfPeople?: number;
	regIdAipv?: string;
	regInventoryNumber?: string;
	regLine?: string;
	regPage?: string;
	regVolume?: string;
	streetName?: string;
	surname?: string;
	year?: number;
};

type OrderKeys =
	| 'ordFirstName'
	| 'ordHouseNumber'
	| 'ordStreetName'
	| 'ordSurname'
	| 'ordYearOfCensus';

type SearchResult = {
	currentPage?: string;
	numberOfPages?: number;
	numberOfRecords?: number;
	orders?: { [K in OrderKeys as string]?: Order };
	results?: Result[];
	shownRecords?: number;
};

enum SearchModes {
	operates = 'operates',
	people = 'people',
}

export const addParam = (
	params: string,
	name: string,
	value?: string,
	suffix?: string,
) => {
	if (!value) return params;

	if (params.length) {
		params += `&${name}=${value}`;
	} else {
		params += `?${name}=${value}`;
	}

	if (suffix) {
		params += suffix;
	}

	return params;
};

const Search: FC = () => {
	const { t } = useTranslation('search');

	const { openViewer } = useContext(ViewerContext);

	const { push, location } = useHistory();

	const classes = useStyles();

	const mode = location.pathname.includes(SearchModes.people)
		? SearchModes.people
		: SearchModes.operates;

	const params = new URLSearchParams(location.search);

	const [data, setData] = useState<SearchResult | undefined>({
		currentPage: params.get('currentPage') ?? undefined,
		orders: {
			ordFirstName: (params.get('ordFirstName') as Order) ?? undefined,
			ordSurname: (params.get('ordSurname') as Order) ?? undefined,
			ordHouseNumber: (params.get('ordHouseNumber') as Order) ?? undefined,
			ordStreetName: (params.get('ordStreetName') as Order) ?? undefined,
			ordYearOfCensus: (params.get('ordYearOfCensus') as Order) ?? undefined,
		},
	});

	const [loading, setLoading] = useState(false);

	const [searchSubmitted, setSearchSubmitted] = useState(false);

	const createParamsQuery = (
		values: Form,
		data?: SearchResult,
		aipvId?: string,
		requestParams?: boolean,
		resetPage?: boolean,
	) => {
		let params = '';

		if (data?.orders) {
			for (const key in data.orders) {
				params = addParam(params, key, data.orders[key]);
			}
		}

		if (!requestParams && values) {
			for (const key in values) {
				params = addParam(params, key, values[key]);
			}
		}

		if (aipvId) {
			params = addParam(params, 'aipvId', aipvId);
		}

		if (!resetPage) {
			if (!requestParams && data?.currentPage) {
				params = addParam(params, 'currentPage', data.currentPage);
			} else if (requestParams && data?.currentPage) {
				params = addParam(params, 'page', data.currentPage);
			}
		}

		return params;
	};

	const setParams = (
		mode: SearchModes,
		values: Form,
		data?: SearchResult,
		aipvId?: string,
	) => {
		const params = createParamsQuery(values, data, aipvId);
		push(`${AppRoutePath.SEARCH}/${mode}${params}`);
	};

	const loadPeople = (aipvId: string, values: Form, requestParams?: string) => {
		setLoading(true);

		let url = `${process.env.PUBLIC_URL}/api/scitacioperaty/search/people/operat/${aipvId}`;

		if (requestParams) {
			url += requestParams;
		}

		try {
			axios.post(url, values).then(response => {
				if (response.data) {
					setData(response.data);
					setParams(SearchModes.people, formik.values, response.data, aipvId);
				}
				setLoading(false);
				setSearchSubmitted(true);
			});
		} catch (e) {
			console.error(e);

			throw e;
		}
	};

	const loadAllPeople = (values: Form, requestParams?: string) => {
		setLoading(true);

		let url = `${process.env.PUBLIC_URL}/api/scitacioperaty/search/people/showAll`;

		if (requestParams) {
			url += requestParams;
		}

		try {
			axios.post(url, values).then(response => {
				if (response.data) {
					setData(response.data);
					setParams(SearchModes.people, values, response.data);
					setLoading(false);
					setSearchSubmitted(true);
				}
			});
		} catch (e) {
			console.error(e);

			throw e;
		}
	};

	const search = async (values: Form, requestParams?: string) => {
		setLoading(true);

		try {
			let url = `${process.env.PUBLIC_URL}/api/scitacioperaty/search`;

			if (requestParams) {
				url += requestParams;
			}

			axios.post<SearchResult>(url, values).then(response => {
				if (response.data) {
					setData(response.data);
					if (
						values.firstName ||
						values.surname ||
						values.inventoryNumber ||
						values.volume ||
						values.page
					) {
						setParams(SearchModes.people, values, response.data);
					} else {
						setParams(SearchModes.operates, values, response.data);
					}
				}
				setLoading(false);
				setSearchSubmitted(true);
			});
		} catch (e) {
			console.error(e);

			throw e;
		}
	};

	const handleChangePage = (
		_: unknown,
		page: number,
		values: Form,
		mode: SearchModes,
	) => {
		if (data?.currentPage && data?.numberOfPages) {
			const currentPage = parseInt(data.currentPage);
			const numberOfPages = data.numberOfPages;
			let nextPage = currentPage;

			if (page === numberOfPages) return;

			if (page === currentPage) {
				nextPage++;
			} else if (page < currentPage) {
				nextPage--;
			}

			const params = createParamsQuery(
				values,
				{
					...data,
					currentPage: nextPage.toString(),
				},
				undefined,
				true,
			);

			loadData(values, params, mode);
		}
	};

	const initialValues = {
		yearOfCensus: params.get('yearOfCensus') ?? '',
		firstName: params.get('firstName') ?? '',
		houseNumber: params.get('houseNumber') ?? '',
		inventoryNumber: params.get('inventoryNumber') ?? '',
		page: params.get('page') ?? '',
		streetName: params.get('streetName') ?? '',
		surname: params.get('surname') ?? '',
		volume: params.get('volume') ?? '',
	};

	const formik = useFormik<Form>({
		initialValues,
		onSubmit: (values: Form) =>
			search(values, createParamsQuery(values, data, undefined, true, true)),
	});

	const loadData = (
		values: Form,
		requestParams?: string,
		mode?: SearchModes,
	) => {
		const aipvId = params.get('aipvId');

		if (!requestParams) {
			requestParams = createParamsQuery(formik.values, data);
		}

		if (aipvId) {
			loadPeople(aipvId, values, requestParams);
		} else if (mode === SearchModes.people) {
			loadAllPeople(values, requestParams);
		} else {
			search(initialValues, requestParams);
		}
	};

	useEffect(() => {
		if (params.toString().length) loadData(formik.values);
	}, []);

	return (
		<>
			<Grid item xs={1} />
			<Grid item xs={10}>
				<form onSubmit={formik.handleSubmit}>
					<FormikProvider value={formik}>
						<Grid item xs={1} />
						<Grid item xs={10}>
							<Typography className={classes.pageTitle} variant="h6" paragraph>
								{t('title')}
							</Typography>
							<Grid container>
								<Grid item xs={12} className={classes.fieldsContainer}>
									<FormikAutocomplete
										id={FormKeys.YEAR_OF_CENSUS}
										name={FormKeys.YEAR_OF_CENSUS}
										label={t('year_of_census')}
										value={formik.values.yearOfCensus}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
									<FormikAutocomplete
										id={FormKeys.STREET_NAME}
										name={FormKeys.STREET_NAME}
										label={t('street')}
										value={formik.values.streetName}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
									<FormikAutocomplete
										id={FormKeys.HOUSE_NUMBER}
										name={FormKeys.HOUSE_NUMBER}
										label={t('house_number')}
										value={formik.values.houseNumber}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
									<FormikAutocomplete
										id={FormKeys.FIRST_NAME}
										name={FormKeys.FIRST_NAME}
										label={t('first_name')}
										value={formik.values.firstName}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
									<FormikAutocomplete
										id={FormKeys.SURNAME}
										name={FormKeys.SURNAME}
										label={t('surname')}
										value={formik.values.surname}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
								</Grid>
							</Grid>
							<Typography className={classes.pageTitle} variant="h6" paragraph>
								{t('title_advanced')}
							</Typography>
							<Grid container>
								<Grid item xs={12} className={classes.fieldsContainer}>
									<FormikAutocomplete
										id={FormKeys.INVENTORY_NUMBER}
										name={FormKeys.INVENTORY_NUMBER}
										label={t('inventary_number')}
										value={formik.values.inventoryNumber}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
									/>
									<FormikAutocomplete
										id={FormKeys.VOLUME}
										name={FormKeys.VOLUME}
										label={t('volume')}
										value={formik.values.volume}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
										helperText={t('name_register')}
									/>
									<FormikAutocomplete
										id={FormKeys.PAGE}
										name={FormKeys.PAGE}
										label={t('page')}
										value={formik.values.page}
										disabled={loading}
										onChange={formik.setFieldValue}
										formValues={formik.values}
										helperText={t('name_register')}
									/>
								</Grid>
							</Grid>
							<Box
								display="flex"
								justifyContent="flex-end"
								alignItems="space-around"
								gridGap="10px"
								marginTop="25px"
							>
								<Button
									type="button"
									disabled={loading || mode === SearchModes.people}
									variant="contained"
									color="secondary"
									onClick={() =>
										loadAllPeople(
											formik.values,
											createParamsQuery(
												formik.values,
												data,
												undefined,
												true,
												true,
											),
										)
									}
								>
									{t('show_people')}
								</Button>
								<Button
									type="submit"
									disabled={loading}
									variant="contained"
									color="primary"
								>
									{t('perform_search')}
								</Button>
							</Box>
						</Grid>
						<Grid item xs={1} />
					</FormikProvider>
				</form>
			</Grid>
			<Grid item xs={1} />
			{(searchSubmitted || loading) && (
				<>
					<Grid item xs={1} />

					<Grid item xs={10}>
						<Typography className={classes.pageTitle} variant="h6" paragraph>
							{t('title_results')}
						</Typography>

						<TableContainer component={Paper}>
							<Table>
								<TableHead>
									<TableRow>
										{mode === SearchModes.operates ? (
											<>
												<TableCell>{t('year')}</TableCell>
												<TableCell>{t('street_name')}</TableCell>
												<TableCell>{t('house_number')}</TableCell>
												<TableCell>{t('persons_by_registry')}</TableCell>
												<TableCell>{t('inventary_number')}</TableCell>
												<TableCell>{t('operates_image')}</TableCell>
											</>
										) : (
											<>
												<TableCell>{t('year')}</TableCell>
												<TableCell>{t('street_name')}</TableCell>
												<TableCell>{t('house_number')}</TableCell>
												<TableCell>{t('operates_image')}</TableCell>
												<TableCell>{t('first_name')}</TableCell>
												<TableCell>{t('surname')}</TableCell>
												<TableCell>{t('page_text')}</TableCell>
											</>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{loading ? (
										<TableRow>
											<TableCell
												align="center"
												colSpan={mode === SearchModes.operates ? 6 : 7}
											>
												<CircularProgress />
											</TableCell>
										</TableRow>
									) : data?.results?.length ? (
										mode === 'operates' ? (
											data.results.map((item, index) => (
												<TableRow key={`row-${index}`}>
													<TableCell>{item.year ?? '-'}</TableCell>
													<TableCell>{item.streetName ?? '-'}</TableCell>
													<TableCell>{item.houseNumber ?? '-'}</TableCell>
													{item.numberOfPeople && item.idAipv ? (
														<TableCell
															onClick={() =>
																loadPeople(item.idAipv ?? '', formik.values)
															}
															style={{
																color: 'red',
																textDecoration: 'underline',
																cursor: 'pointer',
															}}
														>
															{item.numberOfPeople}
														</TableCell>
													) : (
														<TableCell>-</TableCell>
													)}
													<TableCell>{item.inventoryNumber ?? '-'}</TableCell>
													<TableCell>
														{item.existOperatImage && (
															<IconButton
																size="small"
																color="primary"
																onClick={() => openViewer(item.idAipv)}
															>
																<SearchIcon />
															</IconButton>
														)}
													</TableCell>
												</TableRow>
											))
										) : (
											data.results.map((item, index) => (
												<TableRow key={`row-${index}`}>
													<TableCell>{item.year ?? '-'}</TableCell>
													<TableCell>{item.streetName ?? '-'}</TableCell>
													<TableCell>{item.houseNumber ?? '-'}</TableCell>
													<TableCell>
														{item.existOperatImage && (
															<IconButton
																size="small"
																color="primary"
																onClick={() => openViewer(item.idAipv)}
															>
																<SearchIcon />
															</IconButton>
														)}
													</TableCell>
													<TableCell>{item.firstName ?? '-'}</TableCell>
													<TableCell>{item.surname ?? '-'}</TableCell>
													<TableCell>
														{item.regInventoryNumber ?? '-'};{' '}
														{item.regVolume ?? '-'}; str.
														{item.regPage ?? '-'}; ř.{item.regLine ?? '-'}
														{item.existRejstrikImage && (
															<IconButton
																size="small"
																color="primary"
																onClick={() => openViewer(item.regIdAipv)}
															>
																<SearchIcon />
															</IconButton>
														)}
													</TableCell>
												</TableRow>
											))
										)
									) : (
										<TableRow>
											<TableCell
												colSpan={mode === 'operates' ? 6 : 7}
												align="center"
											>
												{t('no_results')}
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>

							{(!loading && data?.results?.length && (
								<TablePagination
									component="div"
									count={data?.numberOfRecords ?? 0}
									rowsPerPage={10}
									rowsPerPageOptions={[10]}
									page={data?.currentPage ? parseInt(data.currentPage) - 1 : 0}
									onChangePage={(e, p) =>
										handleChangePage(e, p, formik.values, mode)
									}
								/>
							)) ||
								null}
						</TableContainer>
						<Viewer />
					</Grid>
					<Grid item xs={1} />
				</>
			)}
		</>
	);
};

const SearchWrapper = () => {
	const viewerContext = useViewer();

	return (
		<ViewerContext.Provider value={viewerContext}>
			<Search />
		</ViewerContext.Provider>
	);
};

export default SearchWrapper;
